import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Radio } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwTextStyled } from '@/_pages/interwill/components/ParticipantContent/styled';
import { IWBeneficiary } from '@/entities/interwills';
import { useActions } from '@/entities/personalIdentifiers/hooks/useActions';
import { isNationalId } from '@/features/sowe-create-appendix-modal/_component/summary/guards/is-national-id';
import { isPassport } from '@/features/sowe-create-appendix-modal/_component/summary/guards/is-passport';
import { Alert } from '@/shared/components/Alert';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { Icon } from '@/shared/uikit/Icon';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectCitizenshipModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type SelectCitizenshipParams = {
  documentId: string;
};

export const SelectCitizenshipModal: FC<SelectCitizenshipModalProps> = ({
  beneficiaryData,
  otherCitizenshipIndex,
  updateBeneficiaryData,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'documentDetails' });

  const modals = useModalsStore();

  const pidActions = useActions();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<SelectCitizenshipParams>();

  const documentOptions = useMemo(() => {
    const types = [PersonalIdentifierType.PASSPORT, PersonalIdentifierType.NATIONAL_ID];

    const pids =
      pidActions.findMany(
        (item) =>
          types.includes(item.personalIdentifierType) &&
          beneficiaryData.primaryCitizenship?.id !== item.id &&
          !beneficiaryData.otherCitizenships?.some((i) => i.id === item.id) &&
          item.beneficiaryId === (beneficiaryData.id ?? 'me'),
      ) ?? [];

    return pids?.map((iPid) => ({
      label: t(
        iPid.personalIdentifierType === PersonalIdentifierType.PASSPORT ? 'passport' : 'nationalId',
        { name: iPid.name },
      ),
      value: iPid.id,
    }));
  }, [pidActions]);

  const onSubmit = (values: SelectCitizenshipParams) => {
    const pidData = pidActions.findById(values.documentId);
    if (!pidData) return;
    if (!isPassport(pidData) && !isNationalId(pidData)) return;

    let dto: Partial<IWBeneficiary> = {};

    const citizenship = {
      id: pidData.id,
      country: pidData.country,
      documentType: pidData.personalIdentifierType,
      number: pidData.number,
      issueDate: String(pidData.dateOfIssue),
    };

    if (otherCitizenshipIndex !== undefined) {
      dto.otherCitizenships = [...(beneficiaryData.otherCitizenships ?? []), citizenship];
    } else {
      dto = {
        firstName: pidData.names,
        lastName: pidData.surnames,
        secondName: pidData.secondName,
        birthDate: pidData.birthDate,
        gender: pidData.gender,
        primaryCitizenship: citizenship,
      };
    }

    updateBeneficiaryData(dto);
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          {!documentOptions.length && <Alert text={t('noDocuments')} />}
          {documentOptions.length > 0 && (
            <Controller
              name={'documentId'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <Form.Item>
                      <SelectTitle>{t('select')}</SelectTitle>
                      <RadioGroupStyled options={documentOptions} {...field} />
                    </Form.Item>
                    {fieldState.error && <Alert color="red" text={t('notSelectedError')} />}
                  </>
                );
              }}
            />
          )}
          <ButtonIwTextStyled
            onClick={() => {
              modals.openModal({
                type: 'CreatePersonalIdentifierModal',
                beneficiaryId: beneficiaryData.id ?? 'me',
                onSubmit: (pidId) => {
                  if (!pidId) return;
                  setValue('documentId', pidId);
                },
              });
            }}
            icon={<Icon name="plus" size="s20" />}
          >
            {t('addBtn')}
          </ButtonIwTextStyled>
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('saveBtn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
  overflow: auto;
  padding: 0 2px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const SelectTitle = styled.b`
  font-size: 16px;
`;

const RadioGroupStyled = styled(Radio.Group)`
  &&& {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 20px;
  }
`;
